




















import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import {
  ContentFeed,
  InputDiscoveryContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useContentFeed from "@/hooks/useContentFeed";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import usePager from "@/hooks/usePager";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import useTags from "@/hooks/useTags";
import useUserFollowings from "@/hooks/useUserFollowings";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { feedFields } from "@/lib/fieldsHelper";
import config from "@/config";
import { CONTENT_FEED_SORTS, EVENTS, sortFields } from "@/const";
import DiscoverView from "./components/DiscoverView";

export default defineComponent({
  name: "PageDiscover",
  components: { DiscoverView },
  props: {
    onFeed: { type: Boolean, default: false },
    tagSlugs: { type: Array as () => string[], default: () => [] },
    hashtagSlugs: { type: Array as () => string[], default: () => [] },
    languages: { type: Array as () => string[], default: () => [] },
    locations: { type: Array as () => string[], default: () => [] },
    sort: { type: String, default: "random" },
    search: { type: Boolean, default: false },
  },
  setup(props) {
    const { tagSlugs, hashtagSlugs, sort } = toRefs(props);

    const { authUser, allowAccess } = useAuthUser();
    const { getTags, tagsBySlug, hashtagsBySlug } = useTags(
      [TagInfoType.USERS, TagInfoType.POSTS],
      tagSlugs,
      hashtagSlugs
    );
    const { contentFeedDiscoverySearch } = useContentFeed();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);

    const loading = ref(false);

    const generateRandomSeed = () => Math.round(Math.random() * 999999) + 1;
    const randomSeed = ref(generateRandomSeed());

    const selectedTags = computed(() => tagsBySlug.value.concat(hashtagsBySlug.value));
    const selectedTagsIds = computed(() => tagsBySlug.value.map(i => i.id));
    const selectedHashtagsIds = computed(() => hashtagsBySlug.value.map(i => i.id));
    const selectedLocations = computed(() => props.locations.map(i => JSON.parse(i)));
    const sortValue = computed(
      () => sortFields.find(field => field.slug === sort.value)?.sort ?? CONTENT_FEED_SORTS.RANDOM
    );

    const filterData = computed<InputDiscoveryContentFeedSearch>(() => ({
      safeForWork: showNotSafeForWork.value ? undefined : true,
      sort: sortValue.value,
      randomSeed: sortValue.value === CONTENT_FEED_SORTS.RANDOM ? randomSeed.value : undefined,
      ...(config.featureTags ? { userTagIds: selectedTagsIds.value.length ? selectedTagsIds.value : undefined } : {}),
      ...(config.featureHashtags
        ? { postTagIds: selectedHashtagsIds.value.length ? selectedHashtagsIds.value : undefined }
        : {}),
      ...(config.featureHideLocalization
        ? {}
        : {
            languages: props.languages.length ? props.languages : undefined,
            locations: selectedLocations.value.length ? selectedLocations.value : undefined,
          }),
    }));

    const { enableScroll, data: contentFeedSearchResult, items, getNextPage, getFirstPage, updateItem } = usePager<
      ContentFeed,
      InputDiscoveryContentFeedSearch
    >((page: number) => {
      return contentFeedDiscoverySearch(
        {
          page,
          ...filterData.value,
        },
        undefined,
        feedFields()
      );
    });

    const { followedUserIds, handleToggleFollow } = useUserFollowings(items);

    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      updateItem(contentFeed, (i1, i2) => i1.id === i2.id);
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedSearchResult.value) return;
      try {
        contentFeedSearchResult.value = {
          ...contentFeedSearchResult.value,
          result: await getPurchasedContentFeeds(
            { authorId: payload.userId, purchasedFeedId: payload.feedId },
            contentFeedSearchResult.value,
            undefined,
            feedFields()
          ),
        };
      } finally {
        if (!payload.feedId) {
          handleToggleFollow(payload.userId, true);
        }
      }
    };

    const loadDiscovery = async () => {
      try {
        loading.value = true;
        await getFirstPage();
      } finally {
        window.scrollTo({ top: 0 });
        loading.value = false;
      }
    };

    const getData = async () => {
      loading.value = true;
      await Promise.all([getPaymentPackages(), getTags()]);
      watch(
        filterData,
        (newValue, oldValue) => {
          if (isEqual(newValue, oldValue)) return;
          loadDiscovery();
        },
        { immediate: true }
      );
    };

    getData();

    watch(authUser, (user: User | null, oldUser: User | null) => {
      if (!oldUser && user && user.accountInfo.lastLoginTime && allowAccess.value) {
        loadDiscovery();
      }
    });

    watch(sortValue, () => {
      if (sortValue.value === CONTENT_FEED_SORTS.RANDOM) {
        randomSeed.value = generateRandomSeed();
      }
    });

    onMounted(async () => {
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
    });

    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
    });

    return {
      loading,
      enableScroll,
      paymentPackages,
      contentFeedSearchResult,
      getNextPage,
      followedUserIds,
      selectedTags,
      selectedTagsIds,
      selectedHashtagsIds,
      selectedLocations,
      sortValue,
      handleToggleFollow,
      onContentFeedUpdate,
    };
  },
});
